/** Submit Contract dialog — replaces the standalone /jobs/new page.
 *
 * Reachable from a button on Dashboard and Contracts list. Accepts the
 * full file-type set the backend now supports (PDF, DOCX, PNG, JPG,
 * TIFF — image formats route through Tesseract OCR backend-side).
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { ApiError } from '@/lib/api';
import { rememberSubmittedJob } from '@/hooks/useJobNotifications';
import { ContractTypeSelector } from './ContractTypeSelector';
import { FileUploadZone } from './FileUploadZone';
import { GateConfigPanel, type GateConfigOverride } from './GateConfigPanel';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

const DEFAULT_GATE_CONFIG: GateConfigOverride = {
  require_all_signatures: true,
  require_compliance_pass: true,
  human_review_on_reject: true,
  blocking_cuad_categories: [],
  jurisdiction_whitelist: [],
};

interface SubmitJobResponse {
  job_id: string;
  status: string;
  estimated_time_seconds: number;
}

interface Props {
  /** Variant for the trigger button; "default" or "outline". */
  variant?: 'default' | 'outline';
  /** Optional label override on the trigger. */
  label?: string;
}

export function SubmitContractDialog({ variant = 'default', label = 'Submit Contract' }: Props) {
  const navigate = useNavigate();
  const { isAdmin, role } = useAuth();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [contractType, setContractType] = useState<string | null>(null);
  const [gateConfig, setGateConfig] = useState<GateConfigOverride>(DEFAULT_GATE_CONFIG);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const canSubmit = file !== null && contractType !== null && !isSubmitting;

  function close() {
    if (isSubmitting) return;
    setOpen(false);
    setFile(null);
    setContractType(null);
    setUploadProgress(null);
  }

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !contractType || isSubmitting) return;
    setIsSubmitting(true);
    setUploadProgress(0);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('declared_contract_type', contractType);
      formData.append('gate_config_override', JSON.stringify(gateConfig));

      const jobId = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_BASE}/api/v1/jobs/`);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status === 202 || xhr.status === 201) {
            const data = JSON.parse(xhr.responseText) as SubmitJobResponse;
            resolve(data.job_id);
          } else {
            reject(new ApiError(xhr.status, `Upload failed (${xhr.status}). Please retry.`));
          }
        };
        xhr.onerror = () => {
          reject(new ApiError(0, 'Upload failed. Check your connection.'));
        };
        xhr.send(formData);
      });

      rememberSubmittedJob(jobId, file.name);
      toast.success(`Submitted: ${file.name}. You'll see the pipeline progress on the job page.`);
      setOpen(false);
      navigate(`/jobs/${jobId}`);
    } catch (err) {
      setUploadProgress(-1);
      toast.error(err instanceof ApiError ? err.message : 'Upload failed.');
    } finally {
      setIsSubmitting(false);
    }
  }, [file, contractType, gateConfig, isSubmitting, navigate]);

  if (role === 'viewer') return null;

  return (
    <>
      <Button variant={variant} onClick={() => { setOpen(true); }} className="gap-1.5">
        <Upload className="h-4 w-4" aria-hidden="true" />
        {label}
      </Button>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Submit contract"
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="bg-card rounded-lg border shadow-lg w-full max-w-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Submit contract for review</h2>
                <p className="text-xs text-muted-foreground">
                  The AI pipeline will analyse the contract and pause at each stage
                  for your review. Accepts PDF, DOCX, and scanned image formats
                  (PNG / JPG / TIFF).
                </p>
              </div>
              <button onClick={close} aria-label="Close" className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
              <FileUploadZone
                file={file}
                onFileChange={setFile}
                uploadProgress={isSubmitting ? (uploadProgress ?? 0) : null}
                isUploading={isSubmitting}
              />
              <ContractTypeSelector
                value={contractType}
                onChange={setContractType}
                disabled={isSubmitting}
              />
              {isAdmin && (
                <GateConfigPanel value={gateConfig} onChange={setGateConfig} />
              )}
              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="ghost" onClick={close} disabled={isSubmitting} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={!canSubmit} className="gap-1.5">
                  <Upload className="h-4 w-4" aria-hidden="true" />
                  {isSubmitting ? 'Submitting…' : 'Submit for review'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
